import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationContract, EnvironmentMode } from '../../contracts/application';
import { ScopeContract } from '../../contracts/scope';
import SecretList from './SecretList';
import ConfirmationModal from './ConfirmationModal';
import { MESSAGES } from '../../constants/messages';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Chip } from '@mui/material';

interface ApplicationCardProps {
    application: ApplicationContract;
    allScopes: ScopeContract[];
    onDelete: (applicationId: string) => void;
    onRename: (applicationId: string, newName: string) => void;
    onCreateSecret: (
        applicationId: string,
        secretName: string,
        expirationDate: string
    ) => void;
    onDeleteSecret: (applicationId: string, secretId: string) => void;
    onToggleScope: (applicationId: string, scopeId: string, isAdding: boolean) => void;
    secretLimit: number;
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({
    application,
    allScopes,
    onDelete,
    onRename,
    onCreateSecret,
    onDeleteSecret,
    onToggleScope,
    secretLimit,
}) => {
    const { t, i18n } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(application.name);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [newSecretName, setNewSecretName] = useState('');
    const [newSecretExpiry, setNewSecretExpiry] = useState('');
    const [selectedScopes, setSelectedScopes] = useState<string[]>(
        application.scopes.map((s) => s.id)
    );
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreatingSecret, setIsCreatingSecret] = useState(false);
    const [secretErrors, setSecretErrors] = useState<{ name?: string; expiry?: string }>({});

    const handleCopy = async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleSaveName = async () => {
        if (editedName.trim() && editedName !== application.name) {
            setIsSaving(true);
            try {
                await onRename(application.id, editedName.trim());
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to rename:', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleCancelEdit = () => {
        setEditedName(application.name);
        setIsEditing(false);
    };

    const isSandbox = useSelector((state: any) => state.Applications.environment) === 'sandbox';

    const handleCreateSecret = async () => {
        const newErrors: { name?: string; expiry?: string } = {};

        if (!newSecretName.trim()) {
            newErrors.name = t('validation.secretNameRequired');
        }

        if (!newSecretExpiry) {
            newErrors.expiry = t('validation.secretExpiryRequired');
        }

        if (Object.keys(newErrors).length > 0) {
            setSecretErrors(newErrors);
            return;
        }

        setIsCreatingSecret(true);
        try {
            await onCreateSecret(application.id, newSecretName.trim(), newSecretExpiry);
            setNewSecretName('');
            setNewSecretExpiry('');
            setSecretErrors({});
            // Keep the card expanded to show the newly created secret
        } catch (error) {
            console.error('Failed to create secret:', error);
        } finally {
            setIsCreatingSecret(false);
        }
    };

    const handleScopesChange = (newScopeIds: string[]) => {
        const oldScopeIds = selectedScopes;
        const added = newScopeIds.filter((id) => !oldScopeIds.includes(id));
        const removed = oldScopeIds.filter((id) => !newScopeIds.includes(id));

        added.forEach((scopeId) => onToggleScope(application.id, scopeId, true));
        removed.forEach((scopeId) => onToggleScope(application.id, scopeId, false));

        setSelectedScopes(newScopeIds);
    };

    const canAddSecret = application.secrets.length < secretLimit;
    const formattedDate = new Date(application.createdAt).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });

    // Get minimum date for expiration (today)
    const getMinExpiryDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    // Get maximum date for expiration (6 months from today)
    const getMaxExpiryDate = () => {
        const maxDate = new Date();
        maxDate.setMonth(maxDate.getMonth() + 6);
        return maxDate.toISOString().split('T')[0];
    };

    const handleToggleExpand = () => {
        setIsExpanded(prev => !prev);
    };

    return (
        <>
            {isCreatingSecret && (
                <div className="fullpage-loader-overlay">
                    <div className="fullpage-loader">
                        <div className="loader-spinner"></div>
                    </div>
                </div>
            )}
            <div className="application-card" data-app-id={application.id} data-expanded={isExpanded}>
            <div className="application-card-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="application-header-left">
                    {isEditing ? (
                        <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="application-name-input"
                            autoFocus
                        />
                    ) : (
                        <h3 className="application-name">{application.name}</h3>
                    )}
                    <span className="application-date">{t('applications.created')} {formattedDate}</span>
                </div>
                <div className="application-header-right">
                    {isEditing ? (
                        <>
                            <button
                                className="application-icon-btn success"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveName();
                                }}
                                title={t('common.save')}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <span className="btn-loader"></span>
                                ) : (
                                    <CheckIcon fontSize="small" />
                                )}
                            </button>
                            <button
                                className="application-icon-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelEdit();
                                }}
                                title={t('common.cancel')}
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="application-icon-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditing(true);
                                }}
                                title={t('applications.editName')}
                            >
                                <EditIcon fontSize="small" />
                            </button>
                            {isExpanded && (
                                <button
                                    className="application-icon-btn danger"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowDeleteConfirm(true);
                                    }}
                                    title={t('applications.deleteApplication')}
                                >
                                    <DeleteIcon fontSize="small" />
                                </button>
                            )}
                        </>
                    )}
                    <button className="application-expand-btn">
                        {isExpanded ? (
                            <ExpandLessIcon fontSize="medium" />
                        ) : (
                            <ExpandMoreIcon fontSize="medium" />
                        )}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="application-card-body">
                    {/* Client ID */}
                    <div className="application-field">
                        <label className="application-label">{t('applications.clientId')}</label>
                        <div className="application-input-wrapper">
                            <input
                                type="text"
                                value={application.applicationId}
                                readOnly
                                className="application-input"
                            />
                            <button
                                className={`application-copy-btn ${
                                    copiedField === 'applicationId' ? 'copied' : ''
                                }`}
                                onClick={() => handleCopy(application.applicationId, 'applicationId')}
                                title={t('applications.copyClientId')}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </button>
                            {copiedField === 'applicationId' && (
                                <span className="copy-success-message">{t('applications.copied')}</span>
                            )}
                        </div>
                    </div>

                    {/* Scopes */}
                    <div className="application-field">
                        <label className="application-label" title={t('applications.scopesCannotBeModified')}>
                            {t('applications.scopes')} ({application.scopes.length} {t('applications.selected')})
                        </label>
                        {application.scopes.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                                {application.scopes.map(scope => (
                                    <Chip 
                                        key={scope.id}
                                        label={scope.name}
                                        size="small"
                                    />
                                ))}
                            </div>
                        ) : (
                            <div style={{ color: '#9ca3af', fontSize: '14px', padding: '8px 0' }}>
                                {t('applications.noScopesSelected')}
                            </div>
                        )}
                    </div>

                    {/* Secrets Section */}
                    <div className="application-secrets-section">
                        <div className="secrets-header">
                            <h4>{t('applications.manageSecrets')}</h4>
                        </div>
                        <SecretList
                            secrets={application.secrets}
                            onDeleteSecret={(secretId) => onDeleteSecret(application.id, secretId)}
                        />

                        {canAddSecret && (
                            <div className="secret-form">
                                <h4 className="secret-form-heading">{t('applications.generateNewSecret')}</h4>
                                <div className="secret-form-field">
                                    <label className="secret-form-label">
                                        {t('applications.secret')} <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newSecretName}
                                        onChange={(e) => {
                                            setNewSecretName(e.target.value);
                                            setSecretErrors({ ...secretErrors, name: undefined });
                                        }}
                                        placeholder={t('applications.secretPlaceholder')}
                                        className={`secret-form-input ${secretErrors.name ? 'error' : ''}`}
                                    />
                                    {secretErrors.name && (
                                        <span className="create-application-error">{secretErrors.name}</span>
                                    )}
                                </div>
                                <div className="secret-form-field">
                                    <label className="secret-form-label">
                                        {t('applications.expiryDate')} <span className="required">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={newSecretExpiry}
                                        onChange={(e) => {
                                            setNewSecretExpiry(e.target.value);
                                            setSecretErrors({ ...secretErrors, expiry: undefined });
                                        }}
                                        min={getMinExpiryDate()}
                                        {...(isSandbox && { max: getMaxExpiryDate() })}
                                        placeholder="MM/DD/YYYY"
                                        className={`secret-form-input ${secretErrors.expiry ? 'error' : ''}`}
                                    />
                                    {secretErrors.expiry && (
                                        <span className="create-application-error">{secretErrors.expiry}</span>
                                    )}
                                </div>
                                <div className="secret-form-actions">
                                    <button
                                        className="secret-form-btn primary"
                                        onClick={handleCreateSecret}
                                        disabled={isCreatingSecret}
                                    >
                                        {t('applications.generateSecret')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={showDeleteConfirm}
                title={t('modals.deleteApplication')}
                message={t('modals.deleteApplicationConfirm')}
                onConfirm={async () => {
                    setIsDeleting(true);
                    try {
                        await onDelete(application.id);
                    } finally {
                        setIsDeleting(false);
                        setShowDeleteConfirm(false);
                    }
                }}
                onCancel={() => setShowDeleteConfirm(false)}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                isLoading={isDeleting}
            />
        </div>
        </>
    );
};

export default ApplicationCard;
